import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {camelCaseToTitleCase} from '@aviary/store/transformation.helpers';
import {ControlRef} from '@aviary/forms/fields/form-fields.types';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {provideControlRefTokenFor} from '@aviary/providers/control-ref.provider';
import {NgClass} from '@angular/common';

// Adding a new color here requires a corresponding update to _slide-toggle.scss.
export type SlideToggleColor = 'primary' | 'success' | 'error' | 'surface';

@Component({
  selector: 'mx-slide-toggle-form-field',
  host: {
    // Slide toggle field labels are indented so they vertically align with the start of the text inside an input, as opposed to the left edge of the input.
    class: 'w-full flex flex-row justify-between items-center'
  },
  template: `
    <div [ngClass]="labelClasses" [class.mx-slide-toggle-disabled-label]="control.disabled">
      {{ overrideLabel ?? camelCaseToTitleCase(fieldName) }}
      @if (description) {
        <div class="text-body-medium text-on-surface-variant">{{ description }}</div>
      }
    </div>

    <mat-slide-toggle [color]="color" [formControl]="control">
      @if (showYesNo) {
        <!--
          We want to keep the slide toggle in the same place, and also the right edge of the text (to maintain a constant right padding width).  Thus,
          keeping those two alignments constant means that we must right-justify the Yes/No text, and that the distance between the toggle and text varies.
        -->
        <p class="w-9 text-end">{{ control.value ? 'Yes' : 'No' }}</p>
      }
    </mat-slide-toggle>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideControlRefTokenFor(SlideToggleFormFieldComponent)],
  imports: [MatSlideToggle, ReactiveFormsModule, NgClass]
})

export class SlideToggleFormFieldComponent implements OnInit, ControlRef {

  @Input({required: true}) public fieldName: string;
  @Input() public overrideLabel?: string;
  @Input() public enabled = true;
  @Input() public showYesNo = false;
  @Input() public color: SlideToggleColor = 'primary';
  @Input() public description?: string;
  @Input() public labelClasses: string = 'text-body-large';

  @Input({required: true})
  public set formGroup(formGroup: ExtendedFormGroup) {
    this.control = getExtendedControlFrom(formGroup, this.fieldName);
  }

  public readonly camelCaseToTitleCase = camelCaseToTitleCase;
  public control: ExtendedFormControl;  // It's tempting to rename this to "formControl", but that's a reserved name and causes confusing errors.  So don't.

  public ngOnInit(): void {
    if (!this.enabled) this.control.disable();
  }
}
